import React, { useEffect, useState } from "react";
import api from "../api/api";
import {
  Container,
  Row,
  Col,
  Card,
  Modal,
  Button,
  Table,
} from "react-bootstrap";
import PageTitle from "../components/common/PageTitle";
import { IconButton } from "@mui/material";
import PreviewIcon from "@mui/icons-material/Preview";
import { useTranslation } from "react-i18next";
import "./AdminManagement.css";

const OrdersManagement = () => {
  const { t } = useTranslation();
  const [editingReactionKey, setEditingReactionKey] = useState(null);
  const [reactionDraft, setReactionDraft] = useState("");
  const [showTextarea, setShowTextarea] = useState(false);
  const [requests, setRequests] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [hoveredUserId, setHoveredUserId] = useState(null);
  let name = JSON.parse(localStorage.getItem("user") || "{}").name;

  useEffect(() => {
    if (selected?.reaction_note) {
      setShowTextarea(true);
    }
  }, [selected]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get("/sensor-installation-requests");
        setRequests(res.data);
      } catch (err) {
        console.error(t("error_fetching_data"), err);
      }
    };
    fetchData();
  }, [t]);

  useEffect(() => {
    const userMap = {};

    requests.forEach((req) => {
      const userId = req.user_id;
      const fieldId = req.field_id;

      if (!userMap[userId]) {
        userMap[userId] = {
          user: req.user,
          fields: {},
        };
      }

      if (!userMap[userId].fields[fieldId]) {
        userMap[userId].fields[fieldId] = {
          field: req.field,
          interested: [],
          requested: [],
        };
      }

      if (req.status === "interested") {
        userMap[userId].fields[fieldId].interested.push(req);
      } else if (req.status === "requested") {
        userMap[userId].fields[fieldId].requested.push(req);
      }
    });

    const rows = [];
    Object.values(userMap).forEach(({ user, fields }) => {
      const fieldList = Object.values(fields);
      fieldList.forEach((fieldData) => {
        rows.push({
          user,
          field: fieldData.field,
          interested: fieldData.interested,
          requested: fieldData.requested,
        });
      });
    });

    setGroupedData(rows);
  }, [requests]);

  const openModal = (item) => {
    setSelected(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelected(null);
    setShowModal(false);
  };

  const getUserId = (user) => user?.id ?? user?.user_uid ?? null;

  const userRowCounts = {};
  groupedData.forEach((item) => {
    const userId = getUserId(item.user);
    userRowCounts[userId] = (userRowCounts[userId] || 0) + 1;
  });

  const renderedUsers = new Set();

  return (
    <Container className="admin-management-page orders-management-page">
      <style>
        {`
          .highlight-user {
            background-color: var(--sf-night-surface-alt, #ededeeff) !important;
            transition: background-color 0.3s ease;
          }
        `}
      </style>

      <Row className="admin-management-heading">
        <PageTitle
          title={t("orders_management")}
          subtitle={t("sensor_installation_overview")}
        />
      </Row>

      <Card className="admin-data-card">
        <Table className="text-center align-middle">
          <thead className="bg-light">
            <tr>
              <th>{t("user")}</th>
              <th>{t("field")}</th>
              <th>{t("interested")}</th>
              <th>{t("requested")}</th>
              <th>{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {groupedData.length > 0 ? (
              groupedData.map((item, index) => {
                const currentUserId = getUserId(item.user);
                const shouldRenderUser = !renderedUsers.has(currentUserId);

                if (shouldRenderUser) {
                  renderedUsers.add(currentUserId);
                }

                const isHovered = hoveredUserId === currentUserId;

                return (
                  <tr
                    key={index}
                    className={isHovered ? "highlight-user" : ""}
                    onMouseEnter={() => setHoveredUserId(currentUserId)}
                    onMouseLeave={() => setHoveredUserId(null)}
                  >
                    {shouldRenderUser && (
                      <td rowSpan={userRowCounts[currentUserId]}>
                        {item.user.name}
                      </td>
                    )}
                    <td>{item.field?.name}</td>
                    <td>
                      {item.interested.length}
                      <br />
                      <small className="text-muted">
                        (
                        {item.interested.filter((r) => !r.reaction_note).length}{" "}
                        {t("no_reaction")})
                      </small>
                    </td>
                    <td>
                      {item.requested.length}
                      <br />
                      <small className="text-muted">
                        ({item.requested.filter((r) => !r.reaction_note).length}{" "}
                        {t("no_reaction")})
                      </small>
                    </td>
                    <td>
                      <IconButton onClick={() => openModal(item)}>
                        <PreviewIcon />
                      </IconButton>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="5">{t("no_data_available")}</td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card>

      <Modal show={showModal} onHide={closeModal} centered size="lg" dialogClassName="admin-management-modal">
        <Modal.Header closeButton>
          <Modal.Title>{t("order_details")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected && (
            <>
              <Row>
                <Col>
                  <strong>{t("user")}</strong> {selected.user?.name}
                </Col>
                <Col>
                  <strong>{t("email")}</strong> {selected.user?.email}
                </Col>
              </Row>
              <Row className="mt-2">
                <Col>
                  <strong>{t("phone")}</strong> {selected.user?.phone}
                </Col>
                <Col>
                  <strong>{t("field")}</strong> {selected.field?.name}
                </Col>
              </Row>

              <h6 className="mt-4">{t("status_history")}</h6>
              <Table bordered size="sm" className="text-center align-middle">
                <thead>
                  <tr>
                    <th>{t("status")}</th>
                    <th>{t("time")}</th>
                    <th>{t("reaction")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const allItems = [
                      ...selected.interested,
                      ...selected.requested,
                    ].sort(
                      (a, b) => new Date(a.created_at) - new Date(b.created_at)
                    );

                    const groupedByReaction = allItems.reduce((acc, item) => {
                      const key =
                        item.reaction_note ||
                        "NO_REACTION_" + (item.reacted_at || item.id);
                      if (!acc[key]) acc[key] = [];
                      acc[key].push(item);
                      return acc;
                    }, {});

                    return Object.entries(groupedByReaction).flatMap(
                      ([reactionKey, items], groupIdx) =>
                        items.map((item, index) => {
                          const isFirst = index === 0;
                          const isEditable =
                            reactionKey.startsWith("NO_REACTION_");
                          const showTextarea =
                            editingReactionKey === reactionKey;

                          return (
                            <tr key={`${groupIdx}-${index}`}>
                              <td>{t(item.status)}</td>
                              <td>
                                {new Date(item.created_at).toLocaleString()}
                              </td>
                              {isFirst && (
                                <td rowSpan={items.length}>
                                  {isEditable ? (
                                    showTextarea ? (
                                      <>
                                        <textarea
                                          className="form-control"
                                          rows={3}
                                          value={reactionDraft}
                                          onChange={(e) =>
                                            setReactionDraft(e.target.value)
                                          }
                                          placeholder={t("enter_reaction_note")}
                                        />
                                        <Button
                                          variant="success"
                                          size="sm"
                                          className="mt-2"
                                          onClick={async () => {
                                            try {
                                              const lastRequest = [
                                                ...selected.requested,
                                                ...selected.interested,
                                              ].sort(
                                                (a, b) =>
                                                  new Date(b.created_at) -
                                                  new Date(a.created_at)
                                              )[0];

                                              if (!lastRequest)
                                                return alert(
                                                  t("no_request_found")
                                                );

                                              await api.post(
                                                `/sensor-installation-requests/react-batch`,
                                                {
                                                  user_id: selected.user?.id,
                                                  field_id: selected.field?.id,
                                                  reaction_note: reactionDraft,
                                                  reacted_by: name,
                                                }
                                              );

                                              alert(t("reaction_saved"));

                                              const res = await api.get(
                                                "/sensor-installation-requests"
                                              );
                                              setRequests(res.data);
                                              closeModal();
                                            } catch (err) {
                                              console.error(err);
                                              alert(t("failed_save_reaction"));
                                            }
                                          }}
                                        >
                                          {t("save")}
                                        </Button>
                                      </>
                                    ) : (
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => {
                                          setEditingReactionKey(reactionKey);
                                          setReactionDraft("");
                                        }}
                                      >
                                        {t("add_reaction")}
                                      </Button>
                                    )
                                  ) : (
                                    <>
                                      <strong>{t("reacted")}</strong>{" "}
                                      {item.reaction_note}
                                      {item.reacted_at && (
                                        <>
                                          <br />
                                          <small className="text-muted">
                                            {t("at")}{" "}
                                            {new Date(
                                              item.reacted_at
                                            ).toLocaleString()}
                                          </small>
                                        </>
                                      )}
                                      {item.reacted_by && (
                                        <div className="text-muted small">
                                          {t("by_user")} #{item.reacted_by}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })
                    );
                  })()}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            {t("close")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default OrdersManagement;
